import React, { useState } from "react";
import { 
  WifiIcon, 
  HighSpeedWifiIcon, 
  StreamingIcon, 
  UsbPowerIcon, 
  AcPowerIcon, 
  LiveTvIcon, 
  SeatbackEntertainmentIcon, 
  LieFlatSeatsIcon, 
  AppleMusicIcon 
} from "../ui/Amenities";

const FlightDetailsDialog = ({ isOpen, onClose, flightData }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!isOpen) return null;

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  // Convert airport codes to detailed city names
  const getDetailedRoute = (route) => {
    const routeMap = {
      'AMD - DEL': 'Ahmedabad, India to New Delhi, India',
      'DEL - JFK': 'New Delhi, India to New York, NY',
      'JFK - ORD': 'New York, NY to Chicago, IL',
      'AMD - BOM': 'Ahmedabad, India to Mumbai, India',
      'BOM - JFK': 'Mumbai, India to New York, NY',
      'EWR - MIA': 'Newark, NJ to Miami, FL',
      'MIA - SAP': 'Miami, FL to San Pedro Sula, Honduras',
      'LAX - SFO': 'Los Angeles, CA to San Francisco, CA',
      'SFO - SEA': 'San Francisco, CA to Seattle, WA',
      'DFW - LAX': 'Dallas, TX to Los Angeles, CA'
    };
    
    return routeMap[route] || route.replace(' - ', ' to ');
  };

  // Convert single flight data to dialog format
  const convertFlightData = (flight) => {
    if (!flight || !flight.segments) return [];
    
    return flight.segments.map((segment, index) => ({
      id: index + 1,
      cityPair: segment.route,
      flightCount: `Flight ${index + 1} of ${flight.segments.length}`,
      route: getDetailedRoute(segment.route),
      date: "Saturday, August 23, 2025", // You might want to pass actual date
      departureTime: index === 0 ? flight.departureTime : "12:15 PM", // Use actual times if available
      arrivalTime: index === flight.segments.length - 1 ? flight.arrivalTime : "2:30 PM",
      flightNumber: segment.flight,
      aircraftType: segment.aircraft,
      travelTime: index === 0 ? flight.duration : "2h 15m", // Use actual duration if available
      connectionTime: index < flight.segments.length - 1 ? "3h 1m" : null,
      onTimePercentage: "97%",
      latePercentage: "0%",
      operator: segment.operator,
      amenities: [
        { icon: "wifi", text: "High-speed Wi-Fi" },
        { icon: "power", text: "AC power" },
        { icon: "usb", text: "USB power" },
        { icon: "device", text: "Personal device streaming" },
        { icon: "tv", text: "Live TV" },
        { icon: "music", text: "Apple Music" }
      ],
      products: [
        { type: "Basic Economy", meals: "Refreshments", bookingCode: "B", class: "Economy" },
        { type: "Main", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Main Plus", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Main Refundable", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Business", meals: "Breakfast", bookingCode: "R", class: "First" },
        { type: "Business Refundable", meals: "Breakfast", bookingCode: "R", class: "First" }
      ]
    }));
  };

  // Use converted flight data or fallback to sample data
  const flights = flightData ? convertFlightData(flightData) : [
    {
      id: 1,
      cityPair: "EWR - MIA",
      flightCount: "Flight 1 of 2",
      route: "Newark, NJ to Miami, FL",
      date: "Saturday, August 23, 2025",
      departureTime: "6:00 AM",
      arrivalTime: "9:14 AM",
      flightNumber: "AA 1196",
      aircraftType: "7M8-Boeing 737MAX 8 Passenger",
      travelTime: "3h 14m",
      connectionTime: "3h 1m",
      onTimePercentage: "97%",
      latePercentage: "0%",
      operator: "",
      amenities: [
        { icon: "wifi", text: "High-speed Wi-Fi" },
        { icon: "power", text: "AC power" },
        { icon: "usb", text: "USB power" },
        { icon: "device", text: "Personal device streaming" },
        { icon: "tv", text: "Live TV" },
        { icon: "music", text: "Apple Music" }
      ],
      products: [
        { type: "Basic Economy", meals: "Refreshments", bookingCode: "B", class: "Economy" },
        { type: "Main", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Main Plus", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Main Refundable", meals: "Refreshments", bookingCode: "V", class: "Economy" },
        { type: "Business", meals: "Breakfast", bookingCode: "R", class: "First" },
        { type: "Business Refundable", meals: "Breakfast", bookingCode: "R", class: "First" }
      ]
    },
    {
      id: 2,
      cityPair: "MIA - SAP",
      flightCount: "Flight 2 of 2",
      route: "Miami, FL to San Pedro Sula, Honduras",
      date: "Saturday, August 23, 2025",
      departureTime: "12:15 PM",
      arrivalTime: "1:00 PM",
      flightNumber: "AA 939",
      aircraftType: "7M8-Boeing 737MAX 8 Passenger",
      travelTime: "2h 45m",
      connectionTime: null,
      onTimePercentage: "94%",
      latePercentage: "3%",
      operator: "",
      amenities: [
        { icon: "wifi", text: "High-speed Wi-Fi" },
        { icon: "power", text: "AC power" },
        { icon: "usb", text: "USB power" },
        { icon: "device", text: "Personal device streaming" },
        { icon: "tv", text: "Live TV" },
        { icon: "music", text: "Apple Music" }
      ],
      products: [
        { type: "Basic Economy", meals: "Beverage service", bookingCode: "B", class: "Economy" },
        { type: "Main", meals: "Beverage service", bookingCode: "V", class: "Economy" },
        { type: "Main Plus", meals: "Beverage service", bookingCode: "V", class: "Economy" },
        { type: "Main Refundable", meals: "Beverage service", bookingCode: "V", class: "Economy" },
        { type: "Business", meals: "Beverage service", bookingCode: "R", class: "Business" },
        { type: "Business Refundable", meals: "Beverage service", bookingCode: "R", class: "Business" }
      ]
    }
  ];

  const currentFlight = flights[activeTab];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-4xl mx-4 max-h-[85vh] overflow-y-auto rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 ">
          <h4 className="text-[32px]  text-aa-blue">Details</h4>
          <button
            onClick={onClose}
            className="w-8 h-8 border-2 border-aa-blue text-aa-blue rounded-full flex items-center justify-center hover:border-aa-dark-blue hover:text-aa-dark-blue transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className=" mx-6 border-b border-gray-200">
          <div className="flex">
            {flights.map((flight, index) => (
              <button
                key={flight.id}
                onClick={() => handleTabChange(index)}
                className={`flex-1 px-4 py-4 text-center transition-colors relative hover:underline ${
                  activeTab === index
                    ? "text-aa-dark-blue bg-white border-t-2 border-l-2 border-r-2 border-aa-dark-blue -mb-px"
                    : "text-aa-blue hover:text-aa-blue hover:bg-gray-50 border-b-3 border-b-transparent"
                }`}
              >
                <div className="font-medium">{flight.cityPair}</div>
                <div className={`text-sm ${
                  activeTab === index ? "text-aa-dark-blue" : "text-aa-blue"
                }`}>{flight.flightCount}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {currentFlight && (
            <div className="space-y-6">
              {/* Flight Route and Date */}
              <div>
                <h3 className=" text-[20px] text-gray-500 mb-2">
                  {currentFlight.route}
                </h3>
                <div className="text-gray-600">{currentFlight.date}</div>
              </div>

              {/* Flight Info Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Flight Times and Aircraft - 5 columns */}
                <div className="lg:col-span-5">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="text-2xl font-medium text-gray-900">
                      {currentFlight.departureTime}
                    </span>
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className="text-2xl font-medium text-gray-900">
                      {currentFlight.arrivalTime}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-normal text-gray-500 text-xs">{currentFlight.flightNumber}</span>
                      <span className="text-gray-500 text-xs">•</span>
                      <span className="text-gray-500 text-xs">{currentFlight.aircraftType}</span>
                    </div>
                    {currentFlight.operator && (
                      <div className="text-xs text-gray-500 italic">
                        {currentFlight.operator}
                      </div>
                    )}
                  </div>
                </div>

                {/* Travel Info - 3 columns */}
                <div className="lg:col-span-3 travel-info">
                  <h2 className="font-semibold text-[14px]">Travel info</h2>
                  <div className="item-info travel-time">
                    <span className="text text-gray-500 mt-2">Travel time:</span>
                    <span className="data">{currentFlight.travelTime}</span>
                  </div>
                  {currentFlight.connectionTime && (
                    <div className="item-info connection-time">
                      <span className="text text-gray-500">Connection time:</span>
                      <span className="data">{currentFlight.connectionTime}</span>
                    </div>
                  )}
                </div>

                {/* Performance Info - 4 columns */}
                <div className="lg:col-span-4 performance-info">
                  <h2 className="font-semibold text-[14px]">Performance<sup>*</sup></h2>
                  <div className="item-info on-time mt-2">
                    <span className="text text-gray-500">On time:</span>
                    <span className="data">{currentFlight.onTimePercentage}<sup>**</sup></span>
                  </div>
                  <div className="item-info late">
                    <span className="text text-gray-500">Late:</span>
                    <span className="data">{currentFlight.latePercentage}</span>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <ul className="flex flex-wrap gap-4">
                  {currentFlight.amenities.map((amenity, index) => {
                    const iconMap = {
                      wifi: WifiIcon,
                      high_speed_wifi: HighSpeedWifiIcon,
                      power: AcPowerIcon,
                      streaming: StreamingIcon,
                      usb: UsbPowerIcon,
                      usb_power: UsbPowerIcon,
                      ac_power: AcPowerIcon,
                      device: StreamingIcon,
                      tv: LiveTvIcon,
                      live_tv: LiveTvIcon,
                      seatback_entertainment: SeatbackEntertainmentIcon,
                      lie_flat_seats: LieFlatSeatsIcon,
                      music: AppleMusicIcon,
                      apple_music: AppleMusicIcon,
                    };
                    
                    const IconComponent = iconMap[amenity.icon.toLowerCase()];
                    
                    return (
                      <li key={index} className="flex items-center space-x-2">
                        {IconComponent ? (
                          <IconComponent className="w-5 h-5" />
                        ) : (
                          <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        )}
                        <span className="text-sm text-gray-700">{amenity.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* Product Details */}
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentFlight.products.map((product, index) => (
                    <div key={index} className="p-4">
                      <div className="font-semibold text-[14px] text-gray-700 mb-3">{product.type}</div>
                      <div className="space-y-0 text-sm">
                        <div>
                          <span className="text-gray-600 text-[12px]">Meals: </span>
                          <span className="font-medium text-gray-600 text-[12px]">{product.meals}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Booking code: </span>
                          <span className="font-medium text-gray-600 text-[12px]">{product.bookingCode}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Class: </span>
                          <span className="font-medium text-gray-600 text-[12px]">{product.class}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* Disclaimers */}
              <div className="w-1/2">
                <div className="space-y-3 text-[10px] text-gray-600">
                  <div>* This is based on information from the month of June 2025</div>
                  <div>** The on-time arrival percentage for the selected flight is based on arrival within 14 minutes after the scheduled arrival as reported monthly to the U.S. Department of Transportation.</div>
                  <div>Amenities and services vary by plane type. If we change planes, your flight may have different amenities and services.</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-aa-blue text-white rounded hover:bg-aa-dark-blue transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightDetailsDialog;
